import { Fragment, useRef } from 'react';
import { createPortal } from 'react-dom';
import { getUserDateFormat } from '@/common/utils/utils';
import type { ChartModel } from '../lib/chartModel';
import type { Device } from '../lib/devices';
import { formatReading, hoverRows } from '../lib/hover';
import { formatTooltipTime } from '../lib/time';
import { useBesideCursor } from '../useBesideCursor';
import type { Hover } from '../useCrosshair';
import styles from './CombinedTooltip.module.css';

/** Props for {@link CombinedTooltip}. */
export interface CombinedTooltipProps {
  hover: Hover;
  models: readonly ChartModel[];
  devices: readonly Device[];
  timeZone: string;
}

/**
 * The hovered instant's readings: a timestamp, a header of visible device names, and one row per chart with each
 * device's value, or a dash where its line isn't drawn. Fixed to the viewport beside the cursor, flipping to stay
 * on screen, so it never shifts or scrolls the chart area.
 */
const CombinedTooltip = ({
  hover,
  models,
  devices,
  timeZone,
}: CombinedTooltipProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const rows = hoverRows(models, devices, hover.at, hover.tolerance);
  const columns = Math.max(devices.length, 1);

  useBesideCursor(ref, hover.clientX, hover.clientY);

  return createPortal(
    <div
      ref={ref}
      role='tooltip'
      className={styles.tooltip}
      style={{ gridTemplateColumns: `auto repeat(${columns}, auto)` }}
    >
      <div className={styles.time}>
        {formatTooltipTime(hover.at, timeZone, getUserDateFormat())}
      </div>
      <span />
      {devices.length === 0 && <span />}
      {devices.map((device) => (
        <span
          key={device.id}
          className={styles.device}
          style={{ color: device.color }}
        >
          {device.label}
        </span>
      ))}
      {rows.map(({ measure, values }) => (
        <Fragment key={measure.key}>
          <span className={styles.label}>{measure.label}</span>
          {values.length === 0 && <span className={styles.missing}>–</span>}
          {values.map((value, i) =>
            value === null ? (
              <span key={devices[i].id} className={styles.missing}>
                –
              </span>
            ) : (
              <span
                key={devices[i].id}
                className={styles.value}
                style={{ color: devices[i].color }}
              >
                {formatReading(value, measure.unit)}
              </span>
            )
          )}
        </Fragment>
      ))}
    </div>,
    document.body
  );
};

export default CombinedTooltip;
